// Importar da agenda do WhatsApp ou dos chats existentes (mockup "Modal
// Importar WhatsApp A"): lista as conexões QR Code da empresa, o usuário
// escolhe uma conectada e o POST (/contacts/import ou /contacts/import/chats)
// vai com whatsappId.
import React, { useEffect, useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import SyncIcon from "@material-ui/icons/Sync";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { formatPhone } from "../../pages/Contacts/contactRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    paper: { width: 480, maxWidth: "calc(100vw - 32px)", margin: 16 },
    body: { gap: 10 },
    label: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary },
    conn: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "11px 14px",
      borderRadius: 12,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      backgroundColor: theme.palette.aeros.surface,
      cursor: "pointer",
      textAlign: "left",
      width: "100%",
      font: "inherit",
      color: "inherit",
      "&:disabled": { opacity: 0.5, cursor: "default" },
    },
    connSelected: {
      borderColor: "rgba(160,68,255,0.4)",
      backgroundColor: "rgba(160,68,255,0.08)",
    },
    radio: {
      width: 16,
      height: 16,
      borderRadius: "50%",
      border: `2px solid ${theme.palette.aeros.textTertiary}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
    },
    radioOn: { borderColor: AEROS.cyan, "& > span": { width: 8, height: 8, borderRadius: "50%", backgroundColor: AEROS.cyan } },
    wa: { color: "#25d366", fontSize: 18 },
    name: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    number: { fontSize: 11, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
    info: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: theme.palette.text.secondary,
      padding: "8px 12px",
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"}`,
      borderRadius: 10,
      marginTop: 4,
      "& .MuiSvgIcon-root": { fontSize: 15, color: stateText(theme, AEROS.cyan, "info") },
    },
    pillOff: {
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
    },
    dot: { width: 5, height: 5, borderRadius: "50%", backgroundColor: "currentColor" },
    empty: { fontSize: 12.5, color: theme.palette.aeros.textTertiary, padding: "12px 0", textAlign: "center" },
  };
});

const isQr = (w) => w.channel === "whatsapp";

const ContactImportPhoneModal = ({ open, onClose, onStarted, mode = "agenda" }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [conns, setConns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const isChats = mode === "chats";

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/whatsapp", { params: { session: 0 } })
      .then(({ data }) => {
        const lista = (Array.isArray(data) ? data : []).filter(isQr);
        setConns(lista);
        const padrao = lista.find((w) => w.isDefault && w.status === "CONNECTED") || lista.find((w) => w.status === "CONNECTED");
        setSelectedId(padrao ? padrao.id : null);
      })
      .catch((err) => toastError(err))
      .finally(() => setLoading(false));
  }, [open]);

  const selected = conns.find((w) => w.id === selectedId);

  const handleImport = async () => {
    setSending(true);
    try {
      await api.post(isChats ? "/contacts/import/chats" : "/contacts/import", { whatsappId: selected.id });
      toast.info(i18n.t(isChats ? "contactImportPhone.startedChats" : "contactImportPhone.started"));
      if (onStarted) onStarted();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={sending ? undefined : onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={m.form}>
        <div className={m.header}>
          <div>
            <div className={m.title}>{i18n.t(isChats ? "contactImportPhone.titleChats" : "contactImportPhone.title")}</div>
            <div className={m.subtitle}>{i18n.t(isChats ? "contactImportPhone.subtitleChats" : "contactImportPhone.subtitle")}</div>
          </div>
          <IconButton className={m.closeButton} onClick={onClose} disabled={sending} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </div>
        <div className={`${m.body} ${classes.body}`}>
          <div className={classes.label}>{i18n.t("contactImportPhone.choose")}</div>
          {loading && (
            <div className={classes.empty}>
              <CircularProgress size={20} />
            </div>
          )}
          {!loading && conns.length === 0 && <div className={classes.empty}>{i18n.t("contactImportPhone.empty")}</div>}
          {conns.map((w) => {
            const on = w.status === "CONNECTED";
            const isSel = selectedId === w.id;
            return (
              <button
                type="button"
                key={w.id}
                className={`${classes.conn} ${isSel ? classes.connSelected : ""}`}
                disabled={!on}
                onClick={() => setSelectedId(w.id)}
                aria-pressed={isSel}
                title={on ? undefined : i18n.t("contactImportPhone.offline")}
              >
                <span className={`${classes.radio} ${isSel ? classes.radioOn : ""}`}>
                  <span />
                </span>
                <WhatsAppIcon className={classes.wa} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div className={classes.name}>{w.name}</div>
                  <div className={classes.number}>{w.number ? formatPhone(w.number) : "—"}</div>
                </span>
                <span className={`${m.pill} ${on ? m.pillSuccess : classes.pillOff}`}>
                  <span className={classes.dot} />
                  {on ? i18n.t("contactImportPhone.connected") : w.status || "QR Code"}
                </span>
              </button>
            );
          })}
          <div className={classes.info}>
            <InfoOutlinedIcon /> {i18n.t("contactImportPhone.noDuplicates")}
          </div>
        </div>
        <div className={m.footer}>
          <div className={m.footerActions}>
            <Button variant="outlined" className={m.cancelButton} onClick={onClose} disabled={sending}>
              {i18n.t("contactImportPhone.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={m.submitButton}
              startIcon={<SyncIcon />}
              onClick={handleImport}
              disabled={sending || !selected || selected.status !== "CONNECTED"}
            >
              {i18n.t("contactImportPhone.import")}
              {sending && <CircularProgress size={22} className={m.buttonProgress} />}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ContactImportPhoneModal;
